import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Container,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeagueMembership } from "../context/LeagueMembershipContext";
import ModalDialog from "../Common/ModalDialog";
import PaginationControls from "../Common/PaginationControls";
import { useRequest } from "../hooks/useRequest";
import { getPlayersUrl, getPlayerUrl } from "../config/api";
import { useLeague } from "../hooks/useLeague";
import { apiPost, apiPatch, apiDelete } from "../utils/apiClient";
import { PaginatedResponse } from "../types/api";

// Type Definitions
interface Player {
  id: number;
  first_name: string;
  last_name: string;
  league_id: number;
  created_at: string;
  updated_at: string;
  discarded_at: string | null;
}

interface PlayerFormElement extends HTMLFormElement {
  firstNameField: HTMLInputElement;
  lastNameField: HTMLInputElement;
}

type SortField = "first_name" | "last_name" | "created_at";
type SortDirection = "asc" | "desc";

// Helper Components
function TableLoadingView() {
  return (
    <Container>
      <Row className="justify-content-md-center">
        <Col md="auto">
          <Spinner animation="grow" /> Loading...
        </Col>
      </Row>
    </Container>
  );
}

// PlayersList Component
function PlayersList({
  onCreateClick,
  onEditClick,
  onDeleteClick,
}: {
  onCreateClick: () => void;
  onEditClick: (player: Player) => void;
  onDeleteClick: (player: Player) => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { canManageLeague } = useLeagueMembership();
  const [sortField, setSortField] = useState<SortField>("last_name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 50;

  // Build paginated URL with sorting
  const buildPaginatedUrl = (
    leagueId: number,
    page: number,
    sortField: SortField,
    sortDirection: SortDirection,
  ): string => {
    const skip = (page - 1) * ITEMS_PER_PAGE;
    const sortParam = sortDirection === "desc" ? `-${sortField}` : sortField;
    return `${getPlayersUrl(
      leagueId,
    )}?limit=${ITEMS_PER_PAGE}&skip=${skip}&sort=${sortParam}`;
  };

  const playersUrl = currentLeague
    ? buildPaginatedUrl(currentLeague.id, currentPage, sortField, sortDirection)
    : undefined;

  const { data: response, error } = useRequest<PaginatedResponse<Player>>(
    playersUrl,
    auth.token,
  );

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
    // Reset to page 1 when sort changes
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Sort indicator component
  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span className="text-muted"> ⇅</span>;
    return <span>{sortDirection === "asc" ? " ▲" : " ▼"}</span>;
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="admin-content">
      {canManageLeague && (
        <Row className="mb-2">
          <Col className="d-flex justify-content-end">
            <Button onClick={() => onCreateClick()} variant="primary">
              New
            </Button>
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          {error && (
            <div className="alert alert-danger" role="alert">
              Error loading players: {error.message}
            </div>
          )}
          {!response && <TableLoadingView />}
          {response &&
            response.data.length === 0 &&
            (canManageLeague ? (
              <p>Click `New` to create your first player.</p>
            ) : (
              <p>This league has no players yet.</p>
            ))}
          {response && response.data.length > 0 && (
            <>
              <Table striped bordered hover className="crud">
                <thead>
                  <tr>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("first_name")}
                    >
                      First Name
                      <SortIndicator field="first_name" />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("last_name")}
                    >
                      Last Name
                      <SortIndicator field="last_name" />
                    </th>
                    <th
                      style={{ cursor: "pointer" }}
                      onClick={() => handleSort("created_at")}
                    >
                      Created Date
                      <SortIndicator field="created_at" />
                    </th>
                    {canManageLeague && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {response.data.map((player: Player) => (
                    <tr key={player.id}>
                      <td>{player.first_name}</td>
                      <td>{player.last_name}</td>
                      <td>{formatDate(player.created_at)}</td>
                      {canManageLeague && (
                        <td align="right">
                          <Button onClick={() => onEditClick(player)}>
                            Edit
                          </Button>
                          &nbsp;
                          <Button
                            variant="danger"
                            onClick={() => onDeleteClick(player)}
                          >
                            Delete
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>

              {response.total > ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={currentPage}
                  totalItems={response.total}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </Col>
      </Row>
    </div>
  );
}

// PlayersFormView Component
function PlayersFormView({
  player,
  onCancelClick,
}: {
  player?: Player;
  onCancelClick: (shouldRefresh?: boolean) => void;
}) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit: React.FormEventHandler<PlayerFormElement> = (event) => {
    event.preventDefault();
    const firstName = event.currentTarget.firstNameField.value.trim();
    const lastName = event.currentTarget.lastNameField.value.trim();

    // Validation
    if (!firstName || firstName.length === 0) {
      setErrorMessage("First name is required");
      return;
    }

    if (!lastName || lastName.length === 0) {
      setErrorMessage("Last name is required");
      return;
    }

    if (!currentLeague) {
      setErrorMessage("No league selected");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    const isEdit = !!player?.id;
    const url =
      isEdit && player
        ? getPlayerUrl(player.id)
        : getPlayersUrl(currentLeague.id);

    const apiFn = isEdit ? apiPatch : apiPost;
    const requestBody = {
      player: {
        first_name: firstName,
        last_name: lastName,
      },
    };

    apiFn(url, requestBody, {
      token: auth.token,
      leagueAbbr: currentLeague.abbr,
    })
      .then((result) => {
        console.debug("Player saved successfully:", result);
        onCancelClick(true); // Returns to list view and triggers refresh
      })
      .catch((error) => {
        console.error("Error saving player:", error);
        setErrorMessage(
          error.message || "An error occurred while saving the player",
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="admin-content">
      <Container>
        <Row>
          <Col md={8}>
            <h1>{player ? "Edit Player" : "New Player"}</h1>

            {errorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="firstNameField">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="First Name"
                  defaultValue={player?.first_name}
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="lastNameField">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Last Name"
                  defaultValue={player?.last_name}
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
              <Button
                onClick={() => onCancelClick()}
                variant="secondary"
                disabled={isSubmitting}
              >
                Cancel
              </Button>{" "}
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Saving...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

// Main Component
function AdminPlayersPage() {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const [content, setContent] = useState<React.ReactNode>(null);
  const [listKey, setListKey] = useState(0);

  // Navigation functions
  function showForm(player?: Player) {
    setContent(<PlayersFormView player={player} onCancelClick={showList} />);
  }

  function showList(refresh?: boolean) {
    if (refresh) {
      setListKey((prev) => prev + 1);
    }
    setContent(
      <PlayersList
        key={listKey}
        onCreateClick={showForm}
        onEditClick={showForm}
        onDeleteClick={confirmDelete}
      />,
    );
  }

  function confirmDelete(player: Player) {
    setContent(
      <div
        className="modal show"
        style={{ display: "block", position: "initial" }}
      >
        <ModalDialog
          titleText="Delete Player Confirmation"
          bodyText={`Are you sure you want to delete this player? (${player.first_name} ${player.last_name})`}
          okText="Delete"
          onCancelClick={() => {
            console.debug("Delete canceled");
            showList();
          }}
          onSaveClick={() => {
            console.debug("Deleting player:", player);

            apiDelete(getPlayerUrl(player.id), {
              token: auth.token,
              ...(currentLeague?.abbr
                ? { leagueAbbr: currentLeague.abbr }
                : {}),
            })
              .then(() => {
                console.debug("Player deleted successfully");
                showList(true); // Trigger refresh after delete
              })
              .catch((error) => {
                console.error("Error deleting player:", error);
                showList();
              });
          }}
        />
      </div>,
    );
  }

  // Initialize with list view when currentLeague changes
  useEffect(() => {
    showList();
    // showList is intentionally not in deps to avoid recreating on every render
    // We only want to reset the list when currentLeague changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLeague]);

  return <>{content}</>;
}

export default AdminPlayersPage;
